import { getApiUrl } from "../api";
import { useEffect, useState } from "react";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { getCustomerRequestNumber } from "../utils/requestReferences";
import { Link } from "./AppLink";
/* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content */



const optionTwoDonationCategoryIcons = [
  "/images/option-two/categories/health-assistance.svg",
  "/images/option-two/categories/education-assistance.svg",
  "/images/option-two/categories/executive-cases-assistance.svg",
  "/images/option-two/categories/housing-assistance.svg",
];

const getOptionTwoDonationCategoryIcon = (category, index = 0) => {
  const title = String(category?.title || "").toLowerCase();
  if (/health|medical|صح/.test(title)) return optionTwoDonationCategoryIcons[0];
  if (/educ|school|تعليم/.test(title)) return optionTwoDonationCategoryIcons[1];
  if (/execut|judicial|court|قض|تنفيذ/.test(title)) return optionTwoDonationCategoryIcons[2];
  if (/hous|home|سكن/.test(title)) return optionTwoDonationCategoryIcons[3];
  return optionTwoDonationCategoryIcons[index % optionTwoDonationCategoryIcons.length];
};



const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};




const parseAmount = (value) => {
  const amount = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const formatAmount = (value) => parseAmount(value).toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const getDonationPercent = (item) => {
  const amount = parseAmount(item.amount);
  const collected = parseAmount(item.collectedAmount);
  if (!amount || !collected) return 0;
  return Math.max(0, Math.min(100, Math.round((collected / amount) * 100)));
};

const CART_STORAGE_KEY = "tarahumDonationCart";

const getStoredCartItems = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    const items = stored ? JSON.parse(stored) : [];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
};

const navigateTo = (path) => {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new Event("app:navigate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const localizedField = (item, field, language, fallback = "") => language === "ar"
  ? item?.[`${field}_ar`] || item?.[field] || item?.[`${field}_en`] || fallback
  : item?.[`${field}_en`] || item?.[field] || item?.[`${field}_ar`] || fallback;

const mapDonationCategory = (category, index, language = "en") => ({
  id: category.id || "donation-category-" + index,
  catid: category.id || 0,
  title: localizedField(category, "title", language),
  ImagePath: getCloudflareImageUrl(category.ImagePath || category.image, "w=160,sharpen=1"),
});


const mapCriticalCase = (item, index, language = "en") => {
  const amount = parseAmount(item.amount);
  const collectedAmount = parseAmount(item.collectedamount || item.collectedAmount);
  const remainingAmount = Math.max(0, amount - collectedAmount);
  return {
    id: item.id || "critical-case-" + index, catid: item.catid || 0, projectid: item.id || 0,
    title: localizedField(item, "title", language), caseNo: getCustomerRequestNumber(item),
    image: getCloudflareImageUrl(item.ImagePath || item.image, "w=800,sharpen=1"),
    amount, collectedAmount, remainingAmount,
    categoryTitle: localizedField(item, "category_title", language, item.categoryTitle || ""),
  };
};


const mapProject = (item, index, language = "en") => ({
  id: item.id || "project-" + index, catid: item.catid || 0, projectid: item.id || 0,
  title: localizedField(item, "title", language),
  image: getCloudflareImageUrl(item.ImagePath || item.image, "w=650,sharpen=1"),
  amount: parseAmount(item.amount),
  categoryTitle: localizedField(item, "category_title", language, item.categoryTitle || ""),
});

const mapNews = (item, index, language = "en") => ({
  id: item.id || "news-" + index, title: localizedField(item, "title", language),
  description: localizedField(item, "description", language), date: item.dt || "",
  image: getCloudflareImageUrl(item.ImagePath || item.image, "w=650,sharpen=1"),
  url: item.url || item.btnurl || "",
});

const CLOUDFLARE_STREAM_HOST = "customer-xsaiqsqgm6hhitqg.cloudflarestream.com";

// This value was saved before the final character of the Stream UID made it to
// the foundation record. Keep the alias here so the public page remains usable
// while older production data is still being served.
const FOUNDATION_STREAM_UID_ALIASES = {
  b7a4ded70586bc39a9a159cc1394971: "b7a4ded70586bc39a9a159cc1394971e",
};

const getFoundationVideo = (value) => {
  if (value?.src && value?.type) return value;

  const video = String(value || "").trim();
  if (!video) {
    return { src: "", type: "video" };
  }

  if (/\.mp4(\?|$)/i.test(video) || video.startsWith("/") || video.startsWith("blob:")) {
    return {
      src: video,
      type: "video",
    };
  }

  const parsedStreamId = video
    .replace(/^https?:\/\/iframe\.videodelivery\.net\//i, "")
    .replace(/^https?:\/\/watch\.cloudflarestream\.com\//i, "")
    .replace(/^https?:\/\/[^/]+\.cloudflarestream\.com\//i, "")
    .replace(/[?#].*$/, "")
    .replace(/\/(?:iframe|watch|manifest\/video\.(?:m3u8|mpd)|thumbnails\/.*)$/i, "")
    .replace(/^\/+|\/+$/g, "");

  const streamId = FOUNDATION_STREAM_UID_ALIASES[parsedStreamId] || parsedStreamId;
  const streamBaseUrl = `https://${CLOUDFLARE_STREAM_HOST}/${streamId}`;

  return {
    src: `${streamBaseUrl}/iframe`,
    poster: `${streamBaseUrl}/thumbnails/thumbnail.jpg?time=1s&height=900`,
    type: "iframe",
  };
};

const getFoundationPlayerUrl = (video, showControls) => {
  if (video.type !== "iframe" || !video.src) return video.src;

  const params = new URLSearchParams({
    autoplay: "true",
    muted: "true",
    loop: "true",
    controls: showControls ? "true" : "false",
    preload: "auto",
  });
  if (video.poster) params.set("poster", video.poster);

  return `${video.src}?${params.toString()}`;
};

const mapFoundationBrief = (item, language = "en") => ({
  title: localizedField(item, "title", language), subtitle: localizedField(item, "subtitle", language),
  description: localizedField(item, "description", language), buttonText: localizedField(item, "btncaption", language),
  buttonUrl: item.btnurl || "", video: getFoundationVideo(item.video),
  image: getCloudflareImageUrl(item.ImagePath, "w=700,sharpen=1"),
  box1Title: localizedField(item, "box1title", language), box1Description: localizedField(item, "box1description", language), box1Icon: getCloudflareImageUrl(item.box1icon, "w=120,sharpen=1"),
  box2Title: localizedField(item, "box2title", language), box2Description: localizedField(item, "box2description", language), box2Icon: getCloudflareImageUrl(item.box2icon, "w=120,sharpen=1"),
  row2Title: localizedField(item, "row2title", language), row2Number: parseAmount(item.row2BeneficiaryFamiliesnumbers), row2BottomTitle: localizedField(item, "row2bottomtitle", language),
  row2BgImage: getCloudflareImageUrl(item.row2bgimge, "w=700,sharpen=1"), row2Image: getCloudflareImageUrl(item.row2ImagePath, "w=700,sharpen=1"),
  row2Description: localizedField(item, "row2lastboxdescription", language), row2Name: localizedField(item, "row2name", language),
  statisticsTitle: localizedField(item, "statisticstitle", language), statisticsDescription: localizedField(item, "statisticsdescription", language),
  statisticsButtonText: localizedField(item, "statisticsbtncaption", language), statisticsButtonUrl: item.statisticsbtnurl || "",
  statisticsImage: getCloudflareImageUrl(item.statisticsImage, "w=800,sharpen=1"),
  statistics: [1, 2, 3, 4].map((number) => ({ title: localizedField(item, `statisticsbox${number}title`, language), value: parseAmount(item[`statistics${number}`]) })).filter((stat) => stat.title || stat.value),
});

const mapBanner = (banner, index, language = "en") => ({
  id: banner.id || "banner-" + index, title: localizedField(banner, "title", language),
  subtitle: localizedField(banner, "subtitle", language), buttonText: localizedField(banner, "btncaption", language, banner.buttonText || ""),
  image: getCloudflareImageUrl(banner.ImagePath || banner.image || banner.img, "w=1900,sharpen=1"), url: banner.url || "",
});
export default function HomeContent({ language = "en", optionTwo = false }) {
  const [banners, setBanners] = useState([]);
  const [donationCategories, setDonationCategories] = useState([]);
  const [criticalCases, setCriticalCases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [newsList, setNewsList] = useState([]);
  const [foundationBrief, setFoundationBrief] = useState({ title: "", subtitle: "", description: "", buttonText: "", buttonUrl: "#", video: { src: "", type: "video" }, image: "", box1Title: "", box1Description: "", box1Icon: "", box2Title: "", box2Description: "", box2Icon: "", row2Title: "", row2Number: 0, row2BottomTitle: "", row2BgImage: "", row2Image: "", row2Description: "", row2Name: "", statisticsTitle: "", statisticsDescription: "", statisticsButtonText: "", statisticsButtonUrl: "#", statisticsImage: "", statistics: [] });
  const [cartValidation, setCartValidation] = useState({});
  const [loadedResources, setLoadedResources] = useState({
    banners: false,
    categories: false,
    criticalCases: false,
    projects: false,
    foundationBrief: false,
    news: false,
  });
  const [isPageReady, setIsPageReady] = useState(false);
  const [isMobileVideo, setIsMobileVideo] = useState(() => (
    typeof window.matchMedia === "function" && window.matchMedia("(max-width: 767px)").matches
  ));
  const foundationVideo = getFoundationVideo(foundationBrief.video);
  const foundationPlayerUrl = getFoundationPlayerUrl(foundationVideo, isMobileVideo);
  const markResourceLoaded = (resource) => {
    setLoadedResources((current) => current[resource] ? current : {
      ...current,
      [resource]: true,
    });
  };
  const copy = language === "ar" ? {
    donate: "تبرع",
    selectDonation: "اختر فئة التبرع",
    donateNow: "تبرع الآن",
    urgentNeeds: "الاحتياجات الإنسانية العاجلة ذات الأولوية",
    criticalCases: "الحالات الحرجة",
    caseNo: "الحالة رقم ",
    donationAmount: "مبلغ التبرع",
    remainingAmount: "المبلغ المتبقي",
    collectedAmount: "المبلغ المحصل",
    enterDonation: "أدخل مبلغ التبرع",
    viewDetails: "عرض التفاصيل",
    addToCart: "أضف إلى السلة",
    currency: "درهم",
    news: "الأخبار",
    latestNews: "أحدث أخبار تراحم",
    newsDescription: "رؤى وأخبار وتحديثات مؤسسة تراحم",
    viewAllNews: "أخبار المؤسسة",
    readMore: "اقرأ المزيد",
    projects: "المشاريع",
    ourProjects: "مشاريعنا",
    allProjects: "مشاريعنا",
    leaderTitle: "نائب رئيس الدولة رئيس مجلس الوزراء حاكم دبي",
  } : {
    donate: "Donate",
    selectDonation: "Choose a Donation Category",
    donateNow: "Donate Now",
    urgentNeeds: "Urgent humanitarian needs prioritized",
    criticalCases: "Critical Cases ",
    caseNo: "Case no ",
    donationAmount: "Donation Amount",
    remainingAmount: "Remaining amount",
    collectedAmount: "Collected amount",
    enterDonation: "Enter Your Donation Amount",
    viewDetails: "View Details",
    addToCart: "Add to Cart",
    currency: "AED",
    news: "News",
    latestNews: "Latest Tarahum News",
    newsDescription: "Insights, trend and updates from the Tarahum ",
    viewAllNews: "View All News",
    readMore: "read more",
    projects: "Projects",
    ourProjects: "Our Projects",
    allProjects: "All Projects",
    leaderTitle: "Vice President of the UAE, Prime Minister, and Ruler of Dubai",
  };

  const handleAddCriticalCaseToCart = (item, event) => {
    event.preventDefault();

    const card = event.currentTarget.closest(".crtic-body");
    const amountInput = card?.querySelector(".crtic-input-wrap input[type='number']");
    const amount = amountInput?.value?.trim() || "";
    const numericAmount = parseAmount(amount);
    const itemId = String(item.id || item.projectid || Date.now());

    if (!amount || numericAmount <= 0) {
      amountInput?.focus();
      amountInput?.classList.add("is-invalid");
      setCartValidation((current) => ({
        ...current,
        [itemId]: "Please enter a valid donation amount before adding this case to cart.",
      }));
      return;
    }

    amountInput?.classList.remove("is-invalid");
    setCartValidation((current) => {
      const next = { ...current };
      delete next[itemId];
      return next;
    });

    const cartItem = {
      id: itemId,
      catid: String(item.catid || 0),
      subcatid: "0",
      projectid: String(item.projectid || item.id || 0),
      title: item.title || "",
      type: "critical-case",
      typeLabel: "Critical Cases",
      caseNo: item.caseNo || "",
      categoryTitle: item.categoryTitle || "",
      amount: String(numericAmount),
      quantity: 1,
      image: item.image || "",
    };

    const existingItems = getStoredCartItems();
    const nextItems = [
      cartItem,
      ...existingItems.filter((existingItem) => existingItem.id !== cartItem.id),
    ];

    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(nextItems));
    navigateTo("/cart");
  };

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;

    const mobileQuery = window.matchMedia("(max-width: 767px)");
    const updateMobileVideo = (event) => setIsMobileVideo(event.matches);
    setIsMobileVideo(mobileQuery.matches);

    if (typeof mobileQuery.addEventListener === "function") {
      mobileQuery.addEventListener("change", updateMobileVideo);
      return () => mobileQuery.removeEventListener("change", updateMobileVideo);
    }

    mobileQuery.addListener(updateMobileVideo);
    return () => mobileQuery.removeListener(updateMobileVideo);
  }, []);

  useEffect(() => {
    const token = getJwtToken();
    const controller = new AbortController();

    const loadBanners = async () => {
      try {
        const headers = { Accept: "application/json" };
        if (token) {
          headers.Authorization = "Bearer " + token;
        }

        const response = await fetch(getApiUrl("/api/GetBannerList.ashx?page=1&pageSize=10"), {
          method: "GET",
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error("Banner API failed with status " + response.status);

        const result = await response.json();
        const apiBanners = Array.isArray(result?.data) ? result.data.map((item, index) => mapBanner(item, index, language)) : [];
        if (apiBanners.length) {
          setBanners(apiBanners);
          window.setTimeout(() => window.dispatchEvent(new Event("react-carousel-refresh")), 0);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load home banners", error);
        }
      } finally {
        if (!controller.signal.aborted) markResourceLoaded("banners");
      }
    };

    loadBanners();
    return () => controller.abort();
  }, [language]);


  useEffect(() => {
    const token = getJwtToken();
    const controller = new AbortController();

    const loadDonationCategories = async () => {
      try {
        const headers = { Accept: "application/json" };
        if (token) {
          headers.Authorization = "Bearer " + token;
        }

        const response = await fetch(getApiUrl("/api/GetDonationCategoryList.ashx?typ=Donation&page=1&pageSize=20"), {
          method: "GET",
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error("Donation category API failed with status " + response.status);

        const result = await response.json();
        const apiCategories = Array.isArray(result?.data) ? result.data.map((item, index) => mapDonationCategory(item, index, language)) : [];
        if (apiCategories.length) {
          // Keep the category carousel populated when the API has fewer than
          // the four cards the home layout is designed to show.
          setDonationCategories(apiCategories.map((category, index) => ({
            ...category,
            ImagePath: optionTwo ? getOptionTwoDonationCategoryIcon(category, index) : category.ImagePath || category.icon,
          })));
          window.setTimeout(() => window.dispatchEvent(new Event("react-carousel-refresh")), 0);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load donation categories", error);
        }
      } finally {
        if (!controller.signal.aborted) markResourceLoaded("categories");
      }
    };

    loadDonationCategories();
    return () => controller.abort();
  }, [language, optionTwo]);


  useEffect(() => {
    const token = getJwtToken();
    const controller = new AbortController();

    const loadCriticalCases = async () => {
      try {
        const headers = { Accept: "application/json" };
        if (token) {
          headers.Authorization = "Bearer " + token;
        }

        const response = await fetch(getApiUrl("/api/GetDonationProjectListHome.ashx?typ=Case&page=1&pageSize=10"), {
          method: "GET",
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error("Critical cases API failed with status " + response.status);

        const result = await response.json();
        const apiCases = Array.isArray(result?.data) ? result.data.map((item, index) => mapCriticalCase(item, index, language)) : [];
        if (apiCases.length) {
          setCriticalCases(apiCases);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load critical cases", error);
        }
      } finally {
        if (!controller.signal.aborted) markResourceLoaded("criticalCases");
      }
    };

    loadCriticalCases();
    return () => controller.abort();
  }, [language]);

  // Refresh only after React has committed the API-backed slides to the DOM.
  // Dispatching from the fetch callback can run before the render is committed,
  // leaving the cold-loaded carousel initialized as an empty/static row.
  useEffect(() => {
    if (!criticalCases.length) return undefined;

    window.dispatchEvent(new Event("react-carousel-refresh"));
    return undefined;
  }, [criticalCases.length]);

  // Re-initialize the project carousel only after React has committed the
  // API-backed project cards. On client-side navigation, refreshing from the
  // fetch callback can otherwise run while the carousel is still empty.
  useEffect(() => {
    if (!projects.length) return undefined;

    window.dispatchEvent(new Event("react-carousel-refresh"));
    return undefined;
  }, [projects.length]);


  useEffect(() => {
    const token = getJwtToken();
    const controller = new AbortController();

    const loadProjects = async () => {
      try {
        const headers = { Accept: "application/json" };
        if (token) {
          headers.Authorization = "Bearer " + token;
        }

        const response = await fetch(getApiUrl("/api/GetDonationProjectList.ashx?typ=Project&page=1&pageSize=10"), {
          method: "GET",
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error("Projects API failed with status " + response.status);

        const result = await response.json();
        const apiProjects = Array.isArray(result?.data) ? result.data.map((item, index) => mapProject(item, index, language)) : [];
        if (apiProjects.length) {
          setProjects(apiProjects);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load projects", error);
        }
      } finally {
        if (!controller.signal.aborted) markResourceLoaded("projects");
      }
    };

    loadProjects();
    return () => controller.abort();
  }, [language]);

  useEffect(() => {
    const token = getJwtToken();
    const controller = new AbortController();

    const loadFoundationBrief = async () => {
      try {
        const headers = { Accept: "application/json" };
        if (token) {
          headers.Authorization = "Bearer " + token;
        }

        const response = await fetch(getApiUrl("/api/GetFoundationBriefList.ashx?page=1&pageSize=1"), {
          method: "GET",
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error("Foundation brief API failed with status " + response.status);

        const result = await response.json();
        const apiBrief = Array.isArray(result?.data) && result.data.length ? mapFoundationBrief(result.data[0], language) : null;
        if (apiBrief) {
          setFoundationBrief(apiBrief);
          window.setTimeout(() => window.dispatchEvent(new Event("home-counter-refresh")), 0);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load foundation brief", error);
        }
      } finally {
        if (!controller.signal.aborted) markResourceLoaded("foundationBrief");
      }
    };

    loadFoundationBrief();
    return () => controller.abort();
  }, [language]);

  useEffect(() => {
    const token = getJwtToken();
    const controller = new AbortController();

    const loadNews = async () => {
      try {
        const headers = { Accept: "application/json" };
        if (token) {
          headers.Authorization = "Bearer " + token;
        }

        const response = await fetch(getApiUrl("/api/GetNewsList.ashx?page=1&pageSize=3"), {
          method: "GET",
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error("News API failed with status " + response.status);

        const result = await response.json();
        const apiNews = Array.isArray(result?.data) ? result.data.map((item, index) => mapNews(item, index, language)) : [];
        if (apiNews.length) {
          setNewsList(apiNews);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load latest news", error);
        }
      } finally {
        if (!controller.signal.aborted) markResourceLoaded("news");
      }
    };

    loadNews();
    return () => controller.abort();
  }, [language]);

  const allResourcesLoaded = Object.values(loadedResources).every(Boolean);

  useEffect(() => {
    if (!allResourcesLoaded) return undefined;

    let cancelled = false;
    const imageUrls = [
      banners[0]?.image,
      ...donationCategories.slice(0, 4).map((item) => item.ImagePath),
      ...criticalCases.slice(0, 3).map((item) => item.image),
      ...projects.slice(0, 3).map((item) => item.image),
      ...newsList.slice(0, 3).map((item) => item.image),
      foundationBrief.image,
      foundationBrief.statisticsImage,
    ].filter(Boolean);

    const preloadImage = (url) => new Promise((resolve) => {
      const image = new Image();
      const finish = () => resolve();
      image.onload = finish;
      image.onerror = finish;
      image.src = url;
      if (image.complete) finish();
    });

    const revealTimer = window.setTimeout(() => {
      if (!cancelled) setIsPageReady(true);
    }, 3500);

    Promise.all(imageUrls.map(preloadImage)).then(() => {
      if (cancelled) return;
      window.clearTimeout(revealTimer);
      setIsPageReady(true);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(revealTimer);
    };
  }, [allResourcesLoaded, banners, donationCategories, criticalCases, projects, newsList, foundationBrief]);

  useEffect(() => {
    const safetyTimer = window.setTimeout(() => setIsPageReady(true), 10000);
    return () => window.clearTimeout(safetyTimer);
  }, []);

  useEffect(() => {
    if (!isPageReady) return undefined;

    // The carousel runtime mounts while the loading screen is visible, so it
    // must be refreshed after React commits the real slides to the document.
    const refresh = () => {
      window.dispatchEvent(new Event("react-carousel-refresh"));
      window.dispatchEvent(new Event("home-counter-refresh"));
    };
    const animationFrame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      refresh();
    });
    const followUpTimer = window.setTimeout(refresh, 200);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(followUpTimer);
    };
  }, [isPageReady]);

  if (!isPageReady) {
    return (
      <section className="home-content-loading" dir={language === "ar" ? "rtl" : "ltr"} role="status" aria-live="polite">
        <div className="home-content-loading-card">
          <img src="/images/img/Tarahum_logo.png" alt={language === "ar" ? "مؤسسة تراحم الخيرية" : "Tarahum Charity Foundation"} />
          <span className="home-content-loading-spinner" aria-hidden="true" />
          <strong>{language === "ar" ? "جاري تجهيز الصفحة" : "Preparing your experience"}</strong>
          <p>{language === "ar" ? "لحظات قليلة، يرجى الانتظار..." : "Just a moment, please..."}</p>
        </div>
      </section>
    );
  }

  return (
    <>
    <div className="banner-slider wow fadeInUp" data-wow-delay="0.2s">
      <div className="swiper">
        <div className="swiper-wrapper">
          {banners.map((banner, index) => (
            <div className="swiper-slide" key={banner.id || index}>
              <div className="banner-item">
                <div className="banner-images">
                  <img src={banner.image} alt={banner.title || "Banner " + (index + 1)} className="ls-is-cached lazyloaded" />
                </div>
                <div className="container">
                  <div className="banner-content">
                    <h2 className="arabic">{banner.title}</h2>
                    <p>{banner.subtitle}</p>
                    <button className="btn-default" onClick={() => { window.location.href = banner.url || "Quick-Donation.html"; }}>
                      {banner.buttonText}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="swiper-pagination"></div>
        <div className="swiper-button-next"></div>
        <div className="swiper-button-prev"></div>
      </div>
    </div>
    <section className="thmb-wrap">
      <div className="container">
        <div className="row section-row">
          <div className="col-xl-12">
            <div className="section-title section-title-center">
              <span className="section-sub-title wow fadeInUp">
                {copy.donate}
              </span>
              <h2 className="text-anime-style-3" data-cursor="-opaque">
                {copy.selectDonation}
              </h2>
            </div>
          </div>
        </div>
        <div className="stry-srv-w donation-category-grid">
          {donationCategories.map((category, index) => (
            <div className="donation-category-grid-item" key={category.id || index}>
                  <div
                    className="stry-card donate-swiper-card openDonation"
                    data-donation-id={category.id}
                    data-catid={category.catid || 0}
                    data-subcatid="0"
                    data-projectid="0"
                    data-donation-title={category.title}
                    data-donation-type="category"
                    data-donation-type-label="Category"
                  >
                    <div className="stry-card-body donate-swiper-body">
                      <div className="stry-icon-wrap donate-swiper-icon">
                        <img src={category.ImagePath} alt={category.title} className="ls-is-cached lazyloaded" />
                      </div>
                      <h3 className="stry-title donate-swiper-title">
                        {category.title}
                      </h3>
                    </div>
                    <button
                      className="openDonation stry-btn donate-swiper-btn"
                      data-donation-id={category.id}
                      data-catid={category.catid || 0}
                      data-subcatid="0"
                      data-projectid="0"
                      data-donation-title={category.title}
                      data-donation-type="category"
                      data-donation-type-label="Category"
                    >
                      {copy.donateNow}
                    </button>
                  </div>
            </div>
          ))}
        </div>
      </div>
    </section>
    <div className="our-services-gold bg-section dark-section">
      <div className="container">
        <div className="row section-row">
          <div className="col-xl-12">
            <div className="section-title section-title-center ttls">
              <span className="section-sub-title wow fadeInUp">
                {copy.urgentNeeds}
              </span>
              <h2 className="text-anime-style-3" data-cursor="-opaque">
                {copy.criticalCases}
              </h2>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-xl-12">
            <div className="testimonial-slider-gold wow fadeInUp">
              <div className="swiper">
                <div className="swiper-wrapper">
                  {criticalCases.map((item, index) => {
                    const percent = getDonationPercent(item);
                    return (
                      <div className="swiper-slide" key={item.id || index}>
                        <div className="crtic">
                          <div className="crtic-card">
                            <div className="crtic-img-wrap">
                              <img src={item.image} alt={item.title} className="ls-is-cached lazyloaded" />
                              {item.caseNo ? (
                                <div className="crtic-case">
                                  {copy.caseNo}{item.caseNo}
                                </div>
                              ) : null}
                            </div>
                            <div className="crtic-body">
                              <h2 className="crtic-title">{item.title}</h2>
                              <div className="crtic-amount-head">
                                <h3>{copy.donationAmount}</h3>
                                <span className="crtic-percent">{percent}{"%"}</span>
                              </div>
                              <div className="crtic-progress">
                                <span style={{ width: percent + "%" }}></span>
                              </div>
                              <div className="crtic-amount-grid">
                                <div className="crtic-amount-box">
                                  <span>{copy.remainingAmount}</span>
                                  <strong>{formatAmount(item.remainingAmount)}{" "}{copy.currency}</strong>
                                </div>
                                <div className="crtic-amount-box">
                                  <span>{copy.collectedAmount}</span>
                                  <strong>{formatAmount(item.collectedAmount)}{" "}{copy.currency}</strong>
                                </div>
                              </div>
                              <div className="crtic-form">
                                <div className="crtic-input-wrap">
                                  <input type="number" placeholder={copy.enterDonation} />
                                  <span>{copy.currency}</span>
                                </div>
                                <button
                                  className="crtic-btn crtic-btn-primary openDonation"
                                  data-donation-id={item.id}
                                  data-catid={item.catid || 0}
                                  data-subcatid="0"
                                  data-projectid={item.projectid || item.id || 0}
                                  data-donation-title={item.title}
                                  data-donation-type="critical-case"
                                  data-donation-type-label="Critical Cases"
                                  data-case-no={item.caseNo || ""}
                                  data-category-title={item.categoryTitle || ""}
                                  data-donation-amount="100"
                                >
                                  {copy.donate}
                                </button>
                              </div>
                              <div className="crtic-actions">
                                <Link to={`/critical-cases-details?id=${encodeURIComponent(item.id || "")}`} className="crtic-btn crtic-btn-outline">
                                  {copy.viewDetails}
                                </Link>
                                <button
                                  type="button"
                                  className="crtic-btn crtic-btn-outline"
                                  onClick={(event) => handleAddCriticalCaseToCart(item, event)}
                                >
                                  {copy.addToCart}
                                  <i className="fa-solid fa-cart-shopping"></i>
                                </button>
                              </div>
                              {cartValidation[String(item.id || item.projectid || "")] ? (
                                <div className="crtic-cart-message" role="alert">
                                  <i className="fa-solid fa-circle-exclamation"></i>
                                  {cartValidation[String(item.id || item.projectid || "")]}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="our-blog pt-40  pb-0">
      <div className="container">
        <div className="p-tabs">
          <div className="row section-row align-items-center">
            <div className="col-xl-6 col-6">
              <div className="section-title">
                <span className="section-sub-title wow fadeInUp">
                  {copy.projects}
                </span>
                <h2 className="text-anime-style-3" data-cursor="-opaque">
                  {copy.ourProjects}
                </h2>
              </div>
            </div>
            <div className="col-xl-6 col-6">
              <div className="section-content-btn" style={{ float: language === "ar" ? "left" : "right" }}>
                <Link to="/our-projects" className="btn-default">
                  {copy.allProjects}
                </Link>
              </div>
            </div>
          </div>
          <div className="ss">
            <div className="tab-content active" id="tab1">
              <div className="testimonial-slider-gold wow fadeInUp">
                <div className="swiper ">
                  <div className="swiper-wrapper" data-cursor-text="Drag">
                    {projects.map((project, index) => (
                      <div className="swiper-slide" key={project.id || index}>
                        <div className="post-item wow fadeInUp">
                          <div className="post-featured-image">
                            <a href="#" data-cursor-text="View">
                              <figure>
                                <img src={project.image} alt={project.title} className="ls-is-cached lazyloaded" />
                              </figure>
                            </a>
                          </div>
                          <div className="post-item-body">
                            <div className="post-item-content">
                              <h2>
                                <a href="#">{project.title}</a>
                              </h2>
                            </div>
                            <div className="post-item-btn">
                              <div className="about-us-btn wow fadeInUp" data-wow-delay="0.8s" style={{ "visibility": "visible", "animationDelay": "0.8s", "animationName": "fadeInUp" }}>
                                <button
                                  className="openDonation btn-default"
                                  data-donation-id={project.id}
                                  data-catid={project.catid || 0}
                                  data-subcatid="0"
                                  data-projectid={project.projectid || project.id || 0}
                                  data-donation-title={project.title}
                                  data-donation-type="project"
                                  data-donation-type-label="Projects"
                                  data-category-title={project.categoryTitle || ""}
                                  data-donation-amount={100}
                                >
                                  {copy.donate}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
      {/* Foundation Brief Start */}
    {foundationBrief.title ? <>
    <div className="about-us">
      <div className="container">
        <div className="row align-items-center">
          <div className="col-xl-6">
            <div className="why-choose-image-box-gold wow fadeInUp" style={{ "visibility": "visible", "animationName": "fadeInUp" }}>
              <div className="why-choose-image-box-1-gold">
                <div className="why-choose-image-gold">
                  <figure className="image-anime wdth foundation-video-frame">
                    {foundationVideo.type === "iframe" ? (
                      <iframe
                        key={foundationPlayerUrl}
                        src={foundationPlayerUrl}
                        title={foundationBrief.title}
                        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <video
                        key={foundationVideo.src}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        controls={isMobileVideo}
                        poster={foundationBrief.image || undefined}
                      >
                        <source src={foundationVideo.src} type="video/mp4" />
                        {"Your browser does not support the video tag."}
                      </video>
                    )}
                  </figure>
                </div>
              </div>
              <div className="why-choose-image-box-2-gold">
                <div className="why-choose-image-gold">
                  <figure className="image-anime">
                    <img src={foundationBrief.image} alt={foundationBrief.title} />
                  </figure>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-6">
            <div className="about-us-content">
              <div className="section-title">
                <span className="section-sub-title  wow fadeInUp">
                  {foundationBrief.subtitle}
                </span>
                <h2 className="text-anime-style-3" data-cursor="-opaque">
                  {foundationBrief.title}
                </h2>
                <p className="wow fadeInUp" data-wow-delay="0.2s">
                  {foundationBrief.description}
                </p>
              </div>
              <div className="about-us-item-list wow fadeInUp" data-wow-delay="0.4s">
                <div className="about-us-item">
                  <div className="icon-box">
                    <img src={foundationBrief.box1Icon} alt={foundationBrief.box1Title} />
                  </div>
                  <div className="about-us-item-content">
                    <h3>
                      {foundationBrief.box1Title}
                    </h3>
                    <p>
                      {foundationBrief.box1Description}
                    </p>
                  </div>
                </div>
                <div className="about-us-item">
                  <div className="icon-box">
                    <img src={foundationBrief.box2Icon} alt={foundationBrief.box2Title} />
                  </div>
                  <div className="about-us-item-content">
                    <h3>
                      {foundationBrief.box2Title}
                    </h3>
                    <p>
                      {foundationBrief.box2Description}
                    </p>
                  </div>
                </div>
              </div>
              <div className="about-us-btn wow fadeInUp" data-wow-delay="0.8s">
                <a href={foundationBrief.buttonUrl} className="btn-default">
                  {foundationBrief.buttonText}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="our-features-gold foundation-impact-section">
      <div className="container">
        <div className="row foundation-impact-row">
          <div className="col-xl-4 col-md-6">
            <div className="feature-item-gold wow fadeInUp" style={{ "backgroundImage": `url(${foundationBrief.row2BgImage})`, "backgroundSize": "cover", "backgroundPosition": "center top", "backgroundColor": "rgb(224, 224, 224)" }}>
              <div className="feature-item-content-gold">
                <h3>
                  {foundationBrief.row2Title}
                </h3>
              </div>
              <div className="feature-item-counter-content-gold">
                <h2>
                  <span className="counter" data-counter-target={foundationBrief.row2Number} style={{ "color": "#222" }}>
                    {foundationBrief.row2Number}
                  </span>
                </h2>
                <p style={{ "color": "#222" }}>
                  {foundationBrief.row2BottomTitle}
                </p>
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-md-6">
            <div className="feature-box-image-gold wow fadeInUp" data-wow-delay="0.2s">
              <figure className="image-anime">
                <img src={foundationBrief.row2Image} alt={foundationBrief.row2Name} />
              </figure>
            </div>
          </div>
          <div className="col-xl-4 col-md-6">
            <div className="feature-item-gold wow fadeInUp" data-wow-delay="0.4s">
              <div className="feature-item-header-gold">
                <div style={{ "width": "60px", "marginBottom": "5px", "marginTop": "10px" }}>
                  <svg width="64px" height="64px" viewBox="0 0 16 16" version="1.1" xmlns="http://www.w3.org/2000/svg" xlink="http://www.w3.org/1999/xlink" fill="#000000">
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                    <g id="SVGRepo_iconCarrier">
                      <path fill="#bcbcbc" d="M9 9v-7h7v7.1c0 4.8-4.5 5.4-4.5 5.4l-0.6-1.4c0 0 2-0.3 2.4-1.9 0.4-1.2-0.4-2.2-0.4-2.2h-3.9z"></path>
                      <path fill="#bcbcbc" d="M0 9v-7h7v7.1c0 4.8-4.5 5.4-4.5 5.4l-0.6-1.4c0 0 2-0.3 2.4-1.9 0.4-1.2-0.4-2.2-0.4-2.2h-3.9z"></path>
                    </g>
                  </svg>
                </div>
                <div className="feature-item-content-gold">
                  <p style={{ "color": "#222" }}>
                    {foundationBrief.row2Description}
                  </p>
                </div>
              </div>
              <div className="feature-item-list-gold">
                <h6>
                  {foundationBrief.row2Name}
                </h6>
                <p className="leader-title">
                  {copy.leaderTitle}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="how-it-work dark-section bg-section" style={{ "marginTop": "20px" }}>
      <div className="container">
        <div className="row">
          <div className="col-xl-6">
            <div className="how-work-content">
              <div className="section-title section-title-center">
                <h2 className="text-anime-style-3" data-cursor="-opaque" style={{ "color": "#114c5c" }}>
                  {foundationBrief.statisticsTitle}
                </h2>
                <p className="wow fadeInUp" data-wow-delay="0.2s" style={{ "color": "#333" }}>
                  {foundationBrief.statisticsDescription}
                </p>
              </div>
              <div className="how-work-btn wow fadeInUp" data-wow-delay="0.4s">
                <a href={foundationBrief.statisticsButtonUrl} className="btn-default ">
                  {foundationBrief.statisticsButtonText}
                </a>
              </div>
            </div>
          </div>
          <div className="col-xl-6">
            <div className="how-it-work-image">
              <figure className="image-anime">
                <img src={foundationBrief.statisticsImage} alt={foundationBrief.statisticsTitle} />
              </figure>
            </div>
          </div>
          <div className="col-xl-12">
            <div className="how-work-item-list">
              <div className="row">
                {foundationBrief.statistics.map((stat, index) => (
                  <div className="col-md-3 col-6" key={stat.title || index}>
                    <div className="cnt-bx">
                      <div className="feature-item-counter-content-gold">
                        <h2>
                          <span className="counter" data-counter-target={stat.value}>
                            {stat.value}
                          </span>
                        </h2>
                        <p>
                          {stat.title}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
     </> : null}
     {/* Foundation Brief End */}
    <div className="our-blog news-section">
      <div className="container">
        <div className="row section-row align-items-center">
          <div className="col-xl-6">
            <div className="section-title">
              <span className="section-sub-title wow fadeInUp">
                {copy.news}
              </span>
              <h2 className="text-anime-style-3" data-cursor="-opaque">
                {copy.latestNews}
              </h2>
            </div>
          </div>
          <div className="col-xl-6">
            <div className="section-content-btn">
              <div className="section-title-content wow fadeInUp" data-wow-delay="0.2s">
                <p>
                  {copy.newsDescription}
                </p>
              </div>
              <div className="section-btn wow fadeInUp" data-wow-delay="0.4s">
                <Link to="/news" className="btn-default">
                  {copy.viewAllNews}
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          {newsList.map((news, index) => (
            <div className="col-xl-4 col-md-6" key={news.id || index}>
              <div className="post-item wow fadeInUp" data-wow-delay={index ? `${index * 0.2}s` : undefined}>
                <div className="post-featured-image">
                  <Link to={`/news-details?id=${encodeURIComponent(news.id || "")}`} data-cursor-text="View">
                    <figure>
                      <img src={news.image} alt={news.title} />
                    </figure>
                  </Link>
                </div>
                <div className="post-item-body">
                  <div className="post-item-content">
                    <h2>
                      <Link to={`/news-details?id=${encodeURIComponent(news.id || "")}`}>
                        {news.title}
                      </Link>
                    </h2>
                  </div>
                  <div className="post-item-btn">
                    <Link to={`/news-details?id=${encodeURIComponent(news.id || "")}`} className="readmore-btn">
                      {copy.readMore}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    </>
  );
}


